import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateProfile, useNeedsProfileSetup } from '../../hooks/queries/useAuth';
import { useProjects } from '../../hooks/queries/useProjects';
import { useSmartRedirect } from '../../hooks/useSmartRedirect';
import { logger } from '../../utils/logger';
import { secureApi } from '../../api/secure';

export function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { mutateAsync: updateProfile } = useUpdateProfile();
  const { refetch: refetchProjects } = useProjects();
  const { needsProfileSetup } = useNeedsProfileSetup();
  const { getSmartRedirectPath } = useSmartRedirect();

  // Use refs to prevent race conditions - refs are synchronous unlike state
  const isProcessingRef = useRef(false);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Prevent multiple simultaneous executions using ref (synchronous check)
    if (isProcessingRef.current) {
      logger.component('Auth callback already processing, skipping');
      return;
    }
    isProcessingRef.current = true;

    const handleAuthCallback = async () => {
      // Set a timeout to prevent infinite loading - redirect after 30 seconds
      timeoutIdRef.current = setTimeout(() => {
        logger.error('Auth callback timeout - redirecting to home after 30s');
        setError('Authentication is taking too long. Please try again.');
        setTimeout(() => navigate('/'), 2000);
      }, 30000);

      try {
        logger.component('Auth callback handler running');

        // Get the auth code from the URL
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const queryParams = new URLSearchParams(window.location.search);

        // Check for invitation parameters
        const invitationId = queryParams.get('invitation');
        const projectId = queryParams.get('project');
        const locale = queryParams.get('locale');

        // Log all query parameters for debugging
        logger.component('Auth callback parameters', {
          invitationId,
          projectId,
          locale,
          fullUrl: window.location.href,
          queryParams: Object.fromEntries([...queryParams]),
          hashParams: Object.fromEntries([...hashParams]),
        });

        // Error handling
        const errorParam = hashParams.get('error') || queryParams.get('error');
        if (errorParam) {
          const errorDescription =
            hashParams.get('error_description') ||
            queryParams.get('error_description') ||
            'Authentication error';
          throw new Error(errorDescription);
        }

        // Handle OAuth callback
        // Supabase automatically exchanges the auth code for a session
        // We just need to verify that the session was created

        logger.component('Getting session from Supabase');
        const { data, error } = await supabase.auth.getSession();

        if (error) {
          logger.error('Error getting session:', error);
          throw error;
        }

        logger.component('Session data:', data?.session ? 'Session exists' : 'No session');

        if (data?.session?.user) {
          logger.component('User authenticated:', data.session.user.id);

          // Log the full user metadata to understand its structure
          logger.component('User metadata after authentication:', {
            provider: data.session.user.app_metadata?.provider,
            email: data.session.user.email,
            metadata: JSON.stringify(data.session.user.user_metadata),
            raw_user_metadata: data.session.user.user_metadata,
            app_metadata: JSON.stringify(data.session.user.app_metadata),
            identities: data.session.user.identities,
          });

          // Extract and save name from Google profile data if available
          // For Google, the name could be in various fields depending on OAuth scopes
          const isGoogleAuth = data.session.user.app_metadata?.provider === 'google';

          // Look for name in identities first (this is likely where Google stores it)
          let googleName = null;
          if (isGoogleAuth) {
            // Enhanced DEBUG logging using console.log since this is visible in production
            console.log('Google Auth DEBUG - User Structure:', {
              hasIdentities: !!data.session.user.identities,
              identitiesCount: data.session.user.identities?.length,
              providerTypes: data.session.user.identities?.map(i => i.provider),
              hasUserMetadata: !!data.session.user.user_metadata,
              userMetadataKeys: data.session.user.user_metadata
                ? Object.keys(data.session.user.user_metadata)
                : [],
              appMetadataKeys: data.session.user.app_metadata
                ? Object.keys(data.session.user.app_metadata)
                : [],
            });

            // DEBUG: log full identity data
            if (data.session.user.identities?.length > 0) {
              console.log(
                'Google Auth DEBUG - Full identities data:',
                JSON.stringify(data.session.user.identities, null, 2)
              );
            }
          }

          if (
            isGoogleAuth &&
            data.session.user.identities &&
            data.session.user.identities.length > 0
          ) {
            const googleIdentity = data.session.user.identities.find(i => i.provider === 'google');
            if (googleIdentity?.identity_data) {
              // Log identity data to see what's available
              console.log('Google Auth DEBUG - Identity data:', googleIdentity.identity_data);

              // Extract name from Google identity data - based on the debug output we can see
              // that both name and full_name are available with the full name
              googleName =
                googleIdentity.identity_data.full_name || googleIdentity.identity_data.name;

              console.log('Google Auth DEBUG - Extracted name from identity data:', googleName);
            }
          }

          // Fallback to user_metadata if not found in identities
          if (!googleName && isGoogleAuth) {
            console.log(
              'Google Auth DEBUG - Checking user_metadata:',
              data.session.user.user_metadata
            );
            googleName =
              data.session.user.user_metadata?.name ||
              data.session.user.user_metadata?.full_name ||
              data.session.user.user_metadata?.given_name;
          }

          // Last resort, use email username with better formatting
          if (!googleName && isGoogleAuth && data.session.user.email) {
            const emailName = data.session.user.email.split('@')[0];
            // Format the email name: capitalize first letter, replace dots with spaces
            googleName = emailName
              .split('.')
              .map(part => part.charAt(0).toUpperCase() + part.slice(1))
              .join(' ');

            console.log('Google Auth DEBUG - Using formatted email as name:', googleName);
          }

          logger.component('Name extraction attempt', {
            isGoogleAuth,
            googleName,
            hasDisplayName: !!data.session.user.user_metadata?.display_name,
          });

          // If user has Google name but no display_name is set, update their profile
          if (isGoogleAuth && googleName && !data.session.user.user_metadata?.display_name) {
            console.log('Setting display_name from Google profile', {
              googleName,
              userId: data.session.user.id,
              metadata: data.session.user.user_metadata,
              locale,
            });

            const userData = {
              ...data.session.user.user_metadata,
              display_name: googleName,
              updated_at: new Date().toISOString(),
            };

            // Add locale preference if provided or not already set
            if (locale && !userData.locale) {
              userData.locale = locale;
            } else if (!userData.locale) {
              // Default to Monday-first calendar (en-GB)
              userData.locale = 'en-GB';
            }

            const { data: updatedUser, error: updateError } = await supabase.auth.updateUser({
              data: userData,
            });

            if (updateError) {
              logger.error('Error updating user display name:', updateError);
              console.error('Error updating user display name:', updateError);
            } else if (updatedUser.user) {
              logger.component('Display name updated successfully');
              console.log('Display name updated successfully', {
                userId: updatedUser.user.id,
                newMetadata: updatedUser.user.user_metadata,
              });
              // Update the user in the React Query cache
              queryClient.setQueryData(['auth', 'user'], updatedUser.user);
            }
          } else {
            // Set the user in the React Query cache
            queryClient.setQueryData(['auth', 'user'], data.session.user);
          }

          // Handle project invitation acceptance if applicable
          if (invitationId && projectId) {
            // Use the hook data instead of store
            logger.component('Processing invitation acceptance', {
              invitationId,
              projectId,
              userId: data.session.user.id,
              email: data.session.user.email,
              isProfileComplete: !needsProfileSetup,
            });

            // For debugging in production
            console.log('Invitation processing DEBUG:', {
              invitationId,
              projectId,
              userId: data.session.user.id,
              email: data.session.user.email,
              isProfileComplete: !needsProfileSetup,
            });

            // First check if the invitation exists
            const { data: checkInvitation, error: checkError } = await supabase
              .from('project_invitations')
              .select('*')
              .eq('id', invitationId)
              .single();

            if (checkError) {
              logger.error('Error checking invitation existence:', checkError);
            } else {
              logger.component('Found invitation:', checkInvitation);
            }

            try {
              // Process specific invitation if present in URL
              if (invitationId && projectId) {
                logger.component('Processing specific invitation from URL parameters', {
                  invitationId,
                  projectId,
                });

                try {
                  const acceptResult = await secureApi.acceptInvitation(invitationId, projectId);
                  logger.component('Specific invitation accepted via secureApi', acceptResult);
                } catch (specificInvitationError) {
                  logger.error('Error processing specific invitation:', specificInvitationError);
                  // Continue even if specific invitation processing fails
                }
              }

              // Process ALL pending invitations for this user's email
              logger.component('Processing all pending invitations for user', {
                email: data.session.user.email,
              });

              try {
                const processResult = await secureApi.processAllPendingInvitations(
                  data.session.user.email
                );

                logger.component('All pending invitations processed', {
                  count: processResult.processedCount,
                  success: processResult.success,
                });

                if (processResult.processedCount > 0) {
                  logger.component('User added to multiple projects through Google Auth', {
                    projectCount: processResult.processedCount,
                    projectIds: processResult.invitations?.map(inv => inv.project_id) || [],
                  });
                }
              } catch (allInvitationsError) {
                logger.error('Error processing all pending invitations:', allInvitationsError);
                // Continue with auth flow even if bulk invitation processing fails
              }
            } catch (invitationError) {
              logger.error('Error in invitation processing wrapper:', invitationError);
              // Continue with auth flow even if invitation processing fails
            }
          }

          // Fetch projects for the user using React Query
          await refetchProjects();

          // Note: Real-time project subscriptions are now handled by the useProjectSubscription hook
          // and no longer need to be set up here

          // Check if profile setup is needed using the React Query hook
          // needsProfileSetup is already available from the hook at the component level
          logger.component('Auth callback navigation decision', {
            needsProfileSetup,
            hasInvitation: !!invitationId,
            hasProject: !!projectId,
            email: data.session.user.email,
          });

          // Clear timeout before any navigation
          if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);

          if (needsProfileSetup) {
            // If profile setup is needed, redirect to profile page
            // Pass invitation data through router state
            logger.component('Redirecting to profile setup with invitation data', {
              hasInvitation: !!invitationId,
              projectId: projectId || null,
            });

            navigate('/profile', {
              state: {
                setupRequired: true,
                invitationId,
                projectId,
              },
            });
          } else if (projectId) {
            // If profile is complete, redirect to the specific project
            logger.component('Redirecting to specific project', {
              projectId,
              hasInvitation: !!invitationId,
            });
            navigate(`/project/${projectId}`);
          } else {
            // Otherwise, use smart redirect
            logger.component('Using smart redirect after login');

            // Get and log the smart redirect decision
            const redirectPath = await getSmartRedirectPath();
            logger.component('Smart redirect decided path', { redirectPath });

            // Navigate to the determined path
            navigate(redirectPath);
          }
        } else {
          // No session found - clear timeout and redirect to home
          if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
          logger.component('No session or user found after OAuth callback');
          navigate('/');
        }
      } catch (err) {
        if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
        logger.error('Error in auth callback:', err);
        setError(err instanceof Error ? err.message : 'Authentication failed');
        // Redirect to home after a delay if there's an error
        setTimeout(() => navigate('/'), 3000);
      }
    };

    handleAuthCallback();

    // Cleanup: clear timeout if component unmounts during auth
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [
    navigate,
    queryClient,
    updateProfile,
    refetchProjects,
    needsProfileSetup,
    getSmartRedirectPath,
  ]);

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-900 text-white p-6">
        <div className="max-w-md w-full bg-gray-800 rounded-xl p-8 shadow-lg">
          <h2 className="text-xl font-bold mb-4 text-red-500">Authentication Error</h2>
          <p className="mb-4">{error}</p>
          <p>Redirecting you to the home page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
      <div className="flex flex-col items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <p>Completing authentication...</p>
      </div>
    </div>
  );
}
